/**
 * What a handover should warn about (never block): the car's STK / servisní prohlídka dates, its open checks and its recorded faults.
 * Pure — the dialog reads the data (the fleet entry, the fleet-wide checks feed, the damage map) and this only judges and words it.
 * The dates are the stock car's; the checks' verdicts are the API's (`stav`), only phrased here.
 */

import { formatDate, pluralCs } from '@/lib/format';
import type { KontrolaTermin, Zavada } from './api';
import { remaining } from './kontroly-helpers';

export interface Upozorneni {
  id: string;
  /** red = it is already wrong, warn = it will be during the rental, neutral = for information. */
  ton: 'red' | 'warn' | 'neutral';
  text: string;
}

/** A stock date as `YYYY-MM-DD` (the stock keeps free text: anything else yields null, i.e. no warning). */
function isoDen(v: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v ?? '');
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function termin(id: string, nazev: string, hodnota: string | null | undefined, dnes: string, konec: string): Upozorneni | null {
  const den = isoDen(hodnota);
  if (!den) return null;
  if (den < dnes) return { id, ton: 'red', text: `${nazev}: platnost skončila ${formatDate(den)}.` };
  if (den <= konec) return { id, ton: 'warn', text: `${nazev}: termín ${formatDate(den)} vyprší během pronájmu.` };
  return null;
}

export function upozorneniNaPredani(p: {
  /** Prague's today, `YYYY-MM-DD`. */
  dnes: string;
  /** The reservation's end, `YYYY-MM-DD HH:mm`. */
  konec: string;
  stk: string | null | undefined;
  serviska: string | null | undefined;
  /** The checks of THIS car (the feed is fleet-wide: the caller filters). */
  kontroly: KontrolaTermin[];
  zavady: Pick<Zavada, 'zavaznost'>[];
}): Upozorneni[] {
  const konec = p.konec.slice(0, 10);
  const out: Upozorneni[] = [];
  const stk = termin('stk', 'STK', p.stk, p.dnes, konec);
  if (stk) out.push(stk);
  const servis = termin('serviska', 'Servisní prohlídka', p.serviska, p.dnes, konec);
  if (servis) out.push(servis);
  for (const k of p.kontroly) {
    const kdy = remaining(k).join(', ');
    out.push({ id: `kontrola-${k.druh}-${k.id ?? 'servis'}`, ton: k.stav === 'po_terminu' ? 'red' : 'warn', text: kdy ? `${k.nazev}: ${kdy}.` : `${k.nazev}.` });
  }
  if (p.zavady.length > 0) {
    const zavazne = p.zavady.filter((z) => z.zavaznost === 'vysoka').length;
    out.push({
      id: 'zavady',
      ton: zavazne > 0 ? 'red' : 'neutral',
      text: `Evidované závady: ${p.zavady.length}${zavazne > 0 ? ` (z toho ${zavazne} ${pluralCs(zavazne, ['závažná', 'závažné', 'závažných'])})` : ''}. Zkontrolujte stav vozu při předání.`,
    });
  }
  return out;
}
